/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal'
import { OperationalFilterChipGroup, OperationalFilterFieldLabel, OperationalFilterPickerRow } from '../../shared/filters/OperationalFilterPrimitives';
import { listSuppliersLocalPaged } from '../../catalog/infra-api';
import { listSuppliersWithPurchases } from '../infra/purchasesRepoSupplierAccount';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import type { PurchaseOrderStatus, PurchaseOrderOrigin } from '../domain/purchaseTypes';

export type PurchaseSortBy = 'date' | 'total' | 'supplier';
export type PurchaseSortDir = 'asc' | 'desc';

export type PurchasesFilterDraft = {
  statusFilter: PurchaseOrderStatus | 'all';
  originFilter: PurchaseOrderOrigin | 'all';
  /** supplierId o 'all'. */
  supplierFilter: string;
  sortBy: PurchaseSortBy;
  sortDir: PurchaseSortDir;
};

export type PurchasesFilterSheetContentProps = {
  draft: PurchasesFilterDraft;
  setDraft: (updater: (prev: PurchasesFilterDraft) => PurchasesFilterDraft) => void;
};

const STATUS_OPTIONS: Array<{ value: PurchaseOrderStatus | 'all'; label: string }> = [
  { value: 'all', label: 'Todas' },
  { value: 'draft', label: 'Borrador' },
  { value: 'sent', label: 'Enviada' },
  { value: 'partial', label: 'Parcial' },
  { value: 'received', label: 'Recibida' },
  { value: 'cancelled', label: 'Anulada' },
  { value: 'closed', label: 'Cerrada' },
];

const ORIGIN_OPTIONS: Array<{ value: PurchaseOrderOrigin | 'all'; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'order', label: 'Pedido' },
  { value: 'direct', label: 'Directa' },
];

const SORT_OPTIONS: Array<{ value: PurchaseSortBy; label: string }> = [
  { value: 'date_desc' as any, label: 'Fecha (más reciente)' },
  { value: 'date_asc' as any, label: 'Fecha (más vieja)' },
  { value: 'total_desc' as any, label: 'Monto (mayor)' },
  { value: 'total_asc' as any, label: 'Monto (menor)' },
  { value: 'supplier_asc' as any, label: 'Proveedor (A-Z)' },
  { value: 'supplier_desc' as any, label: 'Proveedor (Z-A)' },
];

export function PurchasesFilterSheetContent({
  draft,
  setDraft,
}: PurchasesFilterSheetContentProps) {
  const branchId = useSessionStore((s) => s.currentBranchId);
  const [sortSheetOpen, setSortSheetOpen] = useState(false);
  const [supplierSheetOpen, setSupplierSheetOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<Array<{ id: string; name: string }>>([]);

  // Catálogo ∪ proveedores con compras en esta sucursal.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [catalog, withPurchases] = await Promise.all([
          listSuppliersLocalPaged({ limit: 500, offset: 0 }),
          branchId ? listSuppliersWithPurchases(branchId) : Promise.resolve([]),
        ]);
        if (cancelled) return;
        const seen = new Set<string>();
        const merged: Array<{ id: string; name: string }> = [];
        for (const s of catalog) {
          if (seen.has(s.id)) continue;
          seen.add(s.id);
          merged.push({ id: s.id, name: s.name });
        }
        for (const s of withPurchases) {
          if (seen.has(s.supplierId)) continue;
          seen.add(s.supplierId);
          merged.push({ id: s.supplierId, name: s.supplierName });
        }
        merged.sort((a, b) => a.name.localeCompare(b.name));
        setSuppliers(merged);
      } catch {
        // sin proveedores → el picker queda vacío y "Todos" sigue funcionando
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchId]);

  const sortKey = `${draft.sortBy}_${draft.sortDir}` as string;
  const sortLabel = useMemo(
    () => SORT_OPTIONS.find((o) => o.value === sortKey)?.label ?? 'Fecha (más reciente)',
    [sortKey]
  );

  const sortSheetOptions = useMemo(
    () => SORT_OPTIONS.map((o) => ({ id: o.value, name: o.label })),
    []
  );

  const supplierLabel = useMemo(() => {
    if (draft.supplierFilter === 'all') return 'Todos';
    return suppliers.find((s) => s.id === draft.supplierFilter)?.name ?? 'Proveedor';
  }, [draft.supplierFilter, suppliers]);

  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ id: s.id, name: s.name })),
    [suppliers],
  );

  return (
    <View style={styles.container}>
      <View style={styles.section}>
        <OperationalFilterFieldLabel>Estado</OperationalFilterFieldLabel>
        <OperationalFilterChipGroup
          ticketTone
          options={STATUS_OPTIONS}
          value={draft.statusFilter}
          onChange={(statusFilter) => setDraft((p) => ({ ...p, statusFilter }))}
        />
      </View>

      <View style={styles.section}>
        <OperationalFilterFieldLabel>Proveedor</OperationalFilterFieldLabel>
        <OperationalFilterPickerRow
          layout="stack"
          label="Proveedor"
          valueLabel={supplierLabel}
          active={draft.supplierFilter !== 'all'}
          onPress={() => setSupplierSheetOpen(true)}
          accessibilityLabel={`Proveedor, actual: ${supplierLabel}`}
        />
      </View>

      <View style={styles.section}>
        <OperationalFilterFieldLabel>Origen</OperationalFilterFieldLabel>
        <OperationalFilterChipGroup
          ticketTone
          options={ORIGIN_OPTIONS}
          value={draft.originFilter}
          onChange={(originFilter) => setDraft((p) => ({ ...p, originFilter }))}
        />
      </View>

      <View style={styles.section}>
        <OperationalFilterFieldLabel>Orden</OperationalFilterFieldLabel>
        <OperationalFilterPickerRow
          layout="stack"
          label="Ordenar por"
          valueLabel={sortLabel}
          active={sortKey !== 'date_desc'}
          onPress={() => setSortSheetOpen(true)}
          accessibilityLabel={`Ordenar por, actual: ${sortLabel}`}
        />
      </View>

      <MasterFilterSheetModal
        visible={supplierSheetOpen}
        title="Proveedor"
        options={supplierOptions}
        selectedId={draft.supplierFilter === 'all' ? null : draft.supplierFilter}
        allOptionLabel="Todos"
        appearance="ticket"
        onSelect={(id) => {
          setDraft((p) => ({ ...p, supplierFilter: id ?? 'all' }));
          setSupplierSheetOpen(false);
        }}
        onClose={() => setSupplierSheetOpen(false)}
      />

      <MasterFilterSheetModal
        visible={sortSheetOpen}
        title="Ordenar compras"
        options={sortSheetOptions}
        selectedId={sortKey}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          const parts = (id as string).split('_');
          const sortBy = parts[0] as PurchaseSortBy;
          const sortDir = parts[1] as PurchaseSortDir;
          setDraft((p) => ({ ...p, sortBy, sortDir }));
          setSortSheetOpen(false);
        }}
        onClose={() => setSortSheetOpen(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 18,
  },
  section: {
    gap: 8,
  },
});
